import createRaf from "@solid-primitives/raf";
import { makePersisted } from "@solid-primitives/storage";
import { createEffect, createMemo, createSignal, For, onMount, Show, untrack } from "solid-js";

import { tradeIn } from "~/js/casino";
import { game } from "~/js/game";

import { getCurrency } from "~/js/prefs";
import { addStat } from "~/js/stats";
import { createPage } from "~/js/utils";
import { goToPreviousPage, numToString, showPage } from "~/script";
import { user } from "~/userStore";

const rocketImage = new Image(96, 96);
rocketImage.src = "images/rocket.png";

function renderCanvas(
	ctx: CanvasRenderingContext2D,
	cEl: HTMLCanvasElement,
	points: [number, number][],
	activeBet: number,
	multiplier: number,
	gameRunning: boolean,
	nextRoundDelayMs: number,
) {
	const first = points.at(0);
	const last = points.at(-1);
	if (!first || !last) return;

	const scale = window.devicePixelRatio;
	const width = 600;
	const height = 400;

	cEl.width = Math.floor(width * scale);
	cEl.height = Math.floor(height * scale);
	ctx.scale(scale, scale);

	ctx.strokeStyle = "#fff";
	ctx.lineWidth = 4;

	const xScale = Math.min((width - 50) / last[0], 0.1);
	const yScale = Math.min((height - 50) / (last[1] - first[1]), 100);

	// horizonal grid lines
	ctx.fillStyle = "#555";
	for (let i = 0; i < Math.log2(last[1]) + 2; i++) {
		ctx.fillRect(0, height - (2 ** i - first[1]) * yScale, width, 1);
	}

	// tick marks on axes
	ctx.fillStyle = "#fff";
	for (let i = 0; i < width / (1e3 * xScale); i++) ctx.fillRect(1e3 * xScale * i, height - 10, 1, 10);
	for (let i = 0; i < height / yScale; i++) ctx.fillRect(0, height - yScale * i, 10, 1);

	// rocket trail, maybe make this use a curved line later
	for (let i = 1; i < points.length; i++) {
		ctx.beginPath();
		ctx.moveTo(points[i - 1]![0] * xScale, height - (points[i - 1]![1] - first[1]) * yScale);
		ctx.lineTo(points[i]![0] * xScale, height - (points[i]![1] - first[1]) * yScale);
		ctx.strokeStyle = `#f80${Math.floor((i / points.length) * 16).toString(16)}`;
		ctx.stroke();
	}

	// draw rocket
	const tail = points[points.length - 1]!;
	const prev = points[points.length - 2]!;
	const dx = prev[0] * xScale - tail[0] * xScale;
	const dy = height - (prev[1] - first[1]) * yScale - (height - (tail[1] - first[1]) * yScale);
	ctx.save();
	ctx.translate(tail[0] * xScale, height - (tail[1] - first[1]) * yScale);
	ctx.rotate((-Math.PI / 4) * 3 + Math.atan2(dy, dx));
	const rocketSize = 32 + Math.log(tail[1]) * 4;
	ctx.drawImage(rocketImage, -rocketSize / 2, -rocketSize / 2, rocketSize, rocketSize);
	ctx.restore();

	let payoutText = `${(activeBet * multiplier).toFixed(2)} C.`;
	ctx.font = "10px Roboto";

	if (activeBet)
		ctx.fillText(
			payoutText,
			Math.min(last[0] * xScale, width - 10) - ctx.measureText(payoutText).width - 10,
			Math.max(20, height - (last[1] - first[1]) * yScale - 4),
		);

	ctx.fillStyle = gameRunning ? "#fff" : "#f00";
	ctx.font = "60px Roboto";
	payoutText = `x${multiplier.toFixed(2)}`;
	ctx.fillText(payoutText, width / 2 - ctx.measureText(payoutText).width / 2, height / 2);

	if (nextRoundDelayMs > 0) {
		ctx.font = "20px Roboto";
		const t = Math.floor(nextRoundDelayMs / 10) % 100;
		payoutText = `${Math.floor(nextRoundDelayMs / 1e3)}.${"00".substring(t.toString().length)}${t}s to the next crash`;
		ctx.fillText(payoutText, width / 2 - ctx.measureText(payoutText).width / 2, height / 2 + 40);
	}
}

export function CrashPage() {
	let canvasEl: HTMLCanvasElement;
	let canvasContext: CanvasRenderingContext2D;
	let actionButton: HTMLButtonElement;
	let betInput: HTMLInputElement;

	let growthRate = 0.002;
	let crashAt = 1;
	const [multiplier, setMultiplier] = createSignal(1);
	// mapping between time (ms) and multiplier
	let points: [number, number][] = [];
	let lastCashoutMultiplier: number | null = null; // used only to tell if user cashed out low on high crash
	let lastFrameTime = performance.now();
	let nextRoundDelayMs = 0;

	const rocketImage = new Image(96, 96);
	rocketImage.src = "images/rocket.png";
	onMount(() => {
		canvasContext = canvasEl.getContext("2d")!;
		startRound();
		startRaf();
	});

	const maxBet = createMemo(() => {
		return Math.floor(Math.min((user.upgrades.maxbet / 100) * 8, user.tokens));
	});

	function startRound() {
		growthRate = 0.002;
		crashAt = 0.99 / (1 - Math.random());
		setMultiplier(1);
		points = [
			[0, 1],
			[0, 1],
		];

		setGameRunning(true);
		lastCashoutMultiplier = null;
		lastFrameTime = performance.now();
	}

	const [betInputValue, setBetInputValue] = createSignal(maxBet());
	const [activeBet, setActiveBet] = createSignal<number | null>(null);
	const [gameRunning, setGameRunning] = createSignal(false);

	const [automaticCashout, setAutomaticCashout] = createSignal(false);
	const [cashoutThreshold, setCashoutThreshold] = createSignal(2);
	const [autoBet, setAutoBet] = createSignal(false);
	const [instantRestart, setInstantRestart] = createSignal(false);

	const [crashHistory, setCrashHistory] = makePersisted(
		createSignal<number[]>(Array.from({ length: 15 }, () => Math.max(0.99 / (1 - Math.random()), 1))),
		{
			name: "crashHistory",
			serialize: (v) => v.join(","),
			deserialize: (s) => s.split(",").map(Number),
		},
	);

	function placeBet() {
		if (user.tokens < betInputValue()) {
			setBetInputValue(Math.floor(user.tokens));
			return;
		}
		setActiveBet(Math.min(maxBet(), betInputValue()));
		const activeBetVal = activeBet()!;
		if (user.tokens >= activeBetVal) {
			user.tokens -= activeBetVal;
			addStat("spend_chips", activeBetVal);
			game.emit("update");
		}
		actionButton.focus();
	}

	function cashOut() {
		const activeBetVal = activeBet();
		const multiplierVal = multiplier();
		if (!activeBetVal) {
			startRound();
			throw Error("No active bet to cash out");
		}
		if (activeBetVal) {
			user.tokens += activeBetVal * multiplierVal;
			addStat("win_chips", activeBetVal * multiplierVal);
			for (let i = 1; i <= Math.floor(multiplierVal); i++) addStat(`cashout_${i}x`);
			lastCashoutMultiplier = multiplierVal;
			setBetInputValue(Math.min(maxBet(), activeBetVal));
			setActiveBet(null);
		}
		betInput.focus();
	}
	const [, startRaf] = createRaf(handleMyRaf);
	createEffect(() => {
		console.log(activeBet(), untrack(gameRunning));
	});
	function handleMyRaf(time: DOMHighResTimeStamp) {
		let multiplierVal = multiplier();

		const delta = Math.min(time - lastFrameTime, 1e3 / 60);
		const speedScale = Math.max(delta / (1e3 / 60), 1);
		lastFrameTime = time;

		if (gameRunning()) {
			growthRate *= 1.002 * speedScale;
			multiplierVal += growthRate * speedScale;
			points.push([points.at(-1)![0] + delta, Math.min(multiplierVal, crashAt)]);

			if (activeBet() && automaticCashout() && multiplierVal > cashoutThreshold()) {
				// we set the multiplier to the cashout threshold to ensure we cash out at the right time
				multiplierVal = cashoutThreshold();
				cashOut();
			}

			// handle loss
			if (multiplierVal >= crashAt && gameRunning()) {
				multiplierVal = crashAt;

				setCrashHistory((h) => [multiplierVal, ...h].slice(0, 15));

				if (lastCashoutMultiplier && lastCashoutMultiplier < 2 && multiplierVal >= 100) addStat("crash_low_bet_100x");

				if (activeBet() && multiplierVal >= 100) addStat("crash_bet_100x");

				nextRoundDelayMs = autoBet() && instantRestart() ? 100 : 7500;
				setActiveBet(null);
				setGameRunning(false);
			}
		} else {
			nextRoundDelayMs -= delta;
			if (nextRoundDelayMs <= 0) startRound();
			if (autoBet()) placeBet();
		}

		setMultiplier(multiplierVal);
		renderCanvas(canvasContext, canvasEl, points, activeBet() || 0, multiplierVal, gameRunning(), nextRoundDelayMs);
	}

	return (
		<div class="center">
			<h1>Crash</h1>
			<div class="coins">You have {numToString(user.tokens)} chips.</div>
			<div>
				<button class="button" onClick={goToPreviousPage} type="button">
					Back
				</button>
				<button class="button" onClick={tradeIn} type="button">
					Trade in skins
				</button>
				<button class="button" onClick={() => showPage("casino-withdraw")} type="button">
					Withdraw Chips to {getCurrency()}
				</button>
			</div>
			<p class="crash-history-label">Previous Crashes</p>
			<div class="crash-history">
				<For each={crashHistory()}>
					{(crash) => (
						<div class="crash-at" classList={{ "crash-at-2": crash > 2 }}>
							x{crash.toFixed(2)}
						</div>
					)}
				</For>
			</div>

			<div class="crash-body">
				<div class="crash-game-controls">
					<p>Enter the amount of chips you want to bet.</p>
					<div>
						<input
							class="input combo-right"
							onInput={(e) => {
								setBetInputValue(Math.floor(Math.min(e.currentTarget.valueAsNumber, maxBet())));
							}}
							onKeyPress={(e) => {
								if (e.key === "Enter" && activeBet() !== null) {
									placeBet();
								}
							}}
							ref={(el) => {
								betInput = el;
							}}
							style={{ "margin-bottom": "8px" }}
							type="number"
							value={betInputValue()}
						/>
						<button
							class="button combo-button"
							onClick={() => {
								setBetInputValue(maxBet());
							}}
							type="button"
						>
							All-in
						</button>
					</div>

					<div class="crash-controls-section">
						<label class="checkbox-label">
							<input
								checked={automaticCashout()}
								onChange={(e) => {
									setAutomaticCashout(e.currentTarget.checked);
								}}
								type="checkbox"
							/>
							Automatically cash out at:
						</label>
						<input
							class="input"
							disabled={!automaticCashout()}
							onBlur={(e) => {
								const raw = parseFloat(e.target.value);
								const v = Number.isFinite(raw) ? raw : cashoutThreshold();

								setCashoutThreshold(Math.max(v, 1.01));
							}}
							onFocus={(e) => {
								e.target.value = cashoutThreshold().toFixed(2);
							}}
							value={`${cashoutThreshold()}x`}
						/>
						<label class="checkbox-label">
							<input
								checked={autoBet()}
								onChange={(e) => {
									setAutoBet(e.currentTarget.checked);
									if (!e.currentTarget.checked) {
										setInstantRestart(false);
									}
								}}
								type="checkbox"
							/>
							Auto Bet
						</label>
						<Show when={autoBet()}>
							<label class="checkbox-label">
								<input
									checked={instantRestart()}
									onChange={(e) => {
										setInstantRestart(e.currentTarget.checked);
									}}
									type="checkbox"
								/>
								Instant Restart
							</label>
						</Show>
					</div>

					<button
						class="button crash-game-action"
						disabled={(gameRunning() && activeBet() === null) || (!gameRunning() && activeBet() !== null)}
						onClick={() => {
							if (activeBet() !== null && gameRunning()) {
								cashOut();
							} else if (betInputValue() > 0 && !gameRunning()) {
								placeBet();
							}
						}}
						// disable when game is running and there's no active bet
						ref={(el) => {
							actionButton = el;
						}}
						type="button"
					>
						{gameRunning() ? (
							activeBet() === null ? (
								"You can bet next round"
							) : (
								<>Cash Out {Math.floor(activeBet()! * multiplier())} C.</>
							)
						) : activeBet() === null ? (
							<>Bet {Math.min(betInputValue(), maxBet())} C.</>
						) : (
							<>Betted {activeBet()} C.</>
						)}
					</button>
					<p class="crash-description">
						In Crash, you have to Cash Out before the crash. Your bet is multiplied by the multiplier, and your bet is lost if you don't
						Cash Out before the crash.
					</p>
				</div>

				<canvas
					class="crash-game"
					ref={(el) => {
						canvasEl = el;
					}}
					style="width: 600px; height: 400px"
				/>
			</div>
		</div>
	);
}

createPage(() => <CrashPage />, "crash");
